/**
 * @file ButtonElementPanel.tsx
 * @description Панель «Кнопка» (phase19.16 Р2; uiux-design-r2 §4 ASCII):
 * текст со счётчиком, «Куда ведёт», вид [Основная][Контурная][Ссылка]
 * образцами в цветах и форме кнопок сайта, значок, где значок, «На телефоне
 * во всю ширину», «В ряд с соседними кнопками», «В новой вкладке».
 *
 * SB120 этап 3а (uiux-sb120-stage3 §3 «Кнопка»): вид [С заливкой][Контурная]
 * [Ссылка], высота числом (`ButtonHeightField`) и живой образец
 * (`ButtonLiveSample`); заливка, рамка и цвет текста — в группе «Цвет и
 * шрифт» (`ButtonColorFields` + части `typo`).
 */

import { buttonRadiusCss, pickButtonText, SECTION_TEXT_LIMITS, type SiteTheme } from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { CheckRow } from "../../../site-view/CheckRow";
import { OptionCards } from "../../../site-view/OptionCards";
import { Segmented } from "../../../site-view/Segmented";
import { IconPicker } from "../fields/IconPicker";
import { SectionTargetField } from "../fields/SectionTargetField";
import { SectionTextField } from "../fields/SectionTextField";
import { useSectionForm } from "../section-form-context";
import { ButtonHeightField } from "./ButtonHeightField";
import { ButtonLiveSample } from "./ButtonLiveSample";
import type { ElementPanelProps } from "./types";

type Variant = "primary" | "outline" | "link";

/** Образец кнопки в цветах и форме сайта: скругление — число темы или «Пилюля» (SB120 этап 2). */
function ButtonSample({ variant, theme }: { variant: Variant; theme: SiteTheme | null }) {
  const color = theme?.colors.button ?? "var(--primary)";
  const radius = theme ? buttonRadiusCss(theme.shapes.buttonRadius) : "8px";
  if (variant === "link") return <span className="text-sm underline" style={{ color }}>Ссылка</span>;
  const text = theme ? pickButtonText(theme.colors).color : "#fff";
  return (
    <span
      className="inline-flex h-8 items-center px-3 text-sm"
      style={variant === "primary" ? { background: color, color: text, borderRadius: radius } : { border: `1px solid ${color}`, color, borderRadius: radius }}
    >
      Кнопка
    </span>
  );
}

const POSITION = [
  { value: "right" as const, label: "Справа" },
  { value: "left" as const, label: "Слева" },
];

export function ButtonElementPanel({ element, onChange, path }: ElementPanelProps<"button">) {
  const { theme, issueAt } = useSectionForm();
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  const variants: Array<{ value: Variant; label: string }> = [
    { value: "primary", label: "С заливкой" },
    { value: "outline", label: "Контурная" },
    { value: "link", label: "Ссылка" },
  ];
  return (
    <>
      <SectionTextField label="Текст кнопки" path={`${path}.label`} value={element.label} max={SECTION_TEXT_LIMITS.label} onChange={(label) => set({ label })} />
      <SectionTargetField value={element.target} error={issueAt(`${path}.target`)} onChange={(target) => target && set({ target })} />
      <Label>Вид</Label>
      <OptionCards
        aria-label="Вид кнопки"
        columns={3}
        options={variants.map((v) => ({ ...v, preview: <ButtonSample variant={v.value} theme={theme} /> }))}
        value={element.variant}
        onChange={(variant) => set({ variant })}
      />
      <ButtonHeightField
        value={element.height}
        path={path}
        onChange={(height) => {
          const next = { ...element };
          if (height === undefined) delete next.height;
          else next.height = height;
          onChange(next);
        }}
      />
      <ButtonLiveSample element={element} theme={theme} />
      <IconPicker value={element.icon} onChange={(icon) => set({ icon: icon ?? undefined })} />
      {element.icon ? (
        <>
          <Label>Значок стоит</Label>
          <Segmented aria-label="Где значок" options={POSITION} value={element.iconPosition} onChange={(iconPosition) => set({ iconPosition })} />
        </>
      ) : null}
      <CheckRow checked={element.phoneFull} onChange={(phoneFull) => set({ phoneFull })}>
        На телефоне во всю ширину
      </CheckRow>
      <CheckRow checked={element.inline ?? false} onChange={(inline) => set({ inline: inline || undefined })}>
        Ставить в ряд с соседними кнопками
      </CheckRow>
      <p className="type-caption -mt-2 text-on-surface-variant">Кнопки рядом друг с другом переносятся на телефоне.</p>
      <CheckRow checked={element.newTab} onChange={(newTab) => set({ newTab })}>
        Открывать в новой вкладке
      </CheckRow>
    </>
  );
}
